import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { getStatWinner } from 'app/utils/entities/match_data';
import { partitionArray } from 'app/utils/array';
import Dota2PositionIndicator from 'app/components/Dota2PositionIndicator';
import HeroesIcon from 'app/components/HeroesIcon';
import IconWithTooltip from 'app/components/IconWithTooltip';
import TeamName from 'app/components/TeamName';
import Tooltip from 'app/components/Tooltip';
import StatValue from 'app/components/StatValue';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import { getDota2PlayerStatsLines } from './utils';
import style from './style.module.scss';

interface Props {
  data: PlayersStatsBlockMatch;
}

const Dota2Players: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const playerLines = getDota2PlayerStatsLines(data);

  return (
    <BlockTemplate
      title={t('blocks_players-stats_title')}
      withPeriodSelector
      containerClassname={style.playersRolesStatsBlockContent}
      hasData={data.opponents.some(
        opponent => opponent?.stats && Object.values(opponent.stats.players)?.length
      )}
    >
      <TeamName.Container className={style.teamsNames}>
        <TeamName team={data.opponents.at(0)} logoStyle={style.teamNameLogoSize} />
        <TeamName
          team={data.opponents.at(1)}
          logoStyle={style.teamNameLogoSize}
          orientation="reversed"
        />
      </TeamName.Container>
      <ul>
        {playerLines.map((line, position) => {
          const [statLines, [heroesLine]] = partitionArray(line.stats, stat =>
            stat.display === 'hero' ? 1 : 0
          );
          return (
            <li key={position} className={style.line}>
              <div>
                <div className={style.playerName}>
                  <Dota2PositionIndicator position={position + 1} />
                  <span className={style.playerNameOverflow}>
                    {line.team1Player?.name ?? t('all-unknown')}
                  </span>
                </div>
                <HeroesIcon
                  hidePercentage
                  heroes={(heroesLine?.team1Value as HeroStat[]) ?? []}
                  iconClassName={style.playerHero}
                  placeholderSize={3}
                  videogame="dota2"
                />
              </div>
              <ul className={style.statLines}>
                {statLines.map(statLine => {
                  const key = `blocks_players-stats_videogame_${data.videogame_slug}_${statLine.name}`;
                  const [isTeam1StatWinner, isTeam2StatWinner] = getStatWinner(statLine);

                  return (
                    <li key={statLine.name} className={style.statLine}>
                      <span
                        className={classNames(style.statTeam1Value, {
                          [style.statTeamValueWinner]: isTeam1StatWinner,
                          [style.statTeamValueLoser]: isTeam2StatWinner
                        })}
                      >
                        <StatValue
                          value={statLine.team1Value}
                          displayFunction={statLine.displayFunction}
                        />
                      </span>
                      {statLine.Icon ? (
                        <IconWithTooltip
                          label={t(`${key}_tooltip`)}
                          Icon={statLine.Icon}
                          className={style.statLineName}
                        />
                      ) : (
                        <Tooltip content={t(`${key}_tooltip`)} className={style.statLineName}>
                          {t(`${key}_title`)}
                        </Tooltip>
                      )}
                      <span
                        className={classNames(style.statTeam2Value, {
                          [style.statTeamValueWinner]: isTeam2StatWinner,
                          [style.statTeamValueLoser]: isTeam1StatWinner
                        })}
                      >
                        <StatValue
                          value={statLine.team2Value}
                          displayFunction={statLine.displayFunction}
                        />
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div>
                <div className={style.playerNameReversed}>
                  <Dota2PositionIndicator position={position + 1} />
                  <span className={style.playerNameOverflow}>
                    {line.team2Player?.name ?? t('all-unknown')}
                  </span>
                </div>
                <HeroesIcon
                  hidePercentage
                  heroes={(heroesLine?.team2Value as HeroStat[]) ?? []}
                  iconClassName={style.playerHero}
                  orientation="reversed"
                  placeholderSize={3}
                  videogame="dota2"
                />
              </div>
            </li>
          );
        })}
      </ul>
    </BlockTemplate>
  );
};

Dota2Players.displayName = 'Dota2Players';

export default Dota2Players;
